// =============================================================================
// TenantDetailPage — Admin view of a single organization (tenant)
// =============================================================================

import { useState } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/auth-store';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Spinner,
  useConfirm,
} from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TenantPlan {
  id: string;
  name: string;
  slug: string;
  storageLimit: number;
  exportsLimit: number;
  storesLimit: number;
  usersLimit: number;
}

interface TenantMember {
  id: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  role: { name: string; slug: string };
  isActive: boolean;
  acceptedAt: string | null;
}

interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'CANCELLED';
  accountKind: 'MERCHANT' | 'HOUSE';
  companyName: string | null;
  companyEmail: string | null;
  companyPhone: string | null;
  plan: TenantPlan;
  membersCount: number;
  connectionsCount: number;
  createdAt: string;
  updatedAt: string;
  members: TenantMember[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  TenantDetail['status'],
  { label: string; variant: 'success' | 'danger' | 'warning' | 'default' }
> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  SUSPENDED: { label: 'Suspended', variant: 'danger' },
  PENDING_VERIFICATION: { label: 'Pending', variant: 'warning' },
  CANCELLED: { label: 'Cancelled', variant: 'default' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getMemberName(member: TenantMember): string {
  const { firstName, lastName, email } = member.user;
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }
  return email;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const canManageOrgs = user?.permissions?.some(
    (p) => p === 'organization:suspend' || p === 'organization:activate' || p === 'organization:manage',
  );
  const canSetAccountKind = user?.permissions?.includes('wallet:adjust') ?? false;
  const { confirm, dialog: confirmDialog } = useConfirm();

  const tenantQuery = useQuery<TenantDetail, Error>({
    queryKey: ['admin', 'organizations', id],
    queryFn: async () => {
      if (!id) throw new Error('Tenant ID missing');
      const res = await api.get<{ data: TenantDetail }>(
        `/api/v1/admin/organizations/${id}`,
      );
      return res.data;
    },
    enabled: !!id,
  });

  const tenant = tenantQuery.data;

  const queryClient = useQueryClient();

  const suspendMutation = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>(
        `/api/v1/admin/organizations/${id}/suspend`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      setConfirmAction(null);
    },
  });

  const activateMutation = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>(
        `/api/v1/admin/organizations/${id}/activate`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      setConfirmAction(null);
    },
  });

  const accountKindMutation = useMutation({
    mutationFn: (accountKind: 'HOUSE' | 'MERCHANT') =>
      api.patch<{ data: { id: string; accountKind: string } }>(
        `/api/v1/admin/organizations/${id}/account-kind`,
        { accountKind },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
    },
  });

  async function toggleAccountKind() {
    if (!tenant) return;
    const next = tenant.accountKind === 'HOUSE' ? 'MERCHANT' : 'HOUSE';
    const ok = await confirm({
      title:
        next === 'HOUSE'
          ? 'Mark as house account'
          : 'Revert to merchant account',
      description:
        next === 'HOUSE'
          ? `Orders for "${tenant.name}" will settle via an internal subsidy (no Stripe charge). Wallet COGS is still ledgered.`
          : `Orders for "${tenant.name}" will charge the wallet / saved card again.`,
      confirmLabel: next === 'HOUSE' ? 'Mark as House' : 'Mark as Merchant',
      tone: next === 'HOUSE' ? 'danger' : 'default',
    });
    if (ok) accountKindMutation.mutate(next);
  }

  const [confirmAction, setConfirmAction] = useState<
    'suspend' | 'activate' | null
  >(null);

  // --- Loading ---------------------------------------------------------------

  if (tenantQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading tenant details..." />
      </div>
    );
  }

  // --- Error / Not Found -----------------------------------------------------

  if (tenantQuery.isError || !tenant) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-danger">
            {tenantQuery.isError
              ? `Failed to load tenant: ${tenantQuery.error.message}`
              : 'Tenant not found.'}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void tenantQuery.refetch()}
            >
              Retry
            </Button>
            <Link to="/tenants">
              <Button variant="ghost" size="sm">
                Back to Tenants
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    );
  }

  const statusCfg = STATUS_CONFIG[tenant.status];

  // --- Render ----------------------------------------------------------------

  return (
    <div className="space-y-6">
      {confirmDialog}
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-text-tertiary">
        <Link to="/tenants" className="hover:text-text-primary transition-colors">
          Tenants
        </Link>
        <span>/</span>
        <span className="text-text-primary">{tenant.name}</span>
      </nav>

      {/* Organization info card */}
      <Card>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-semibold text-text-primary">{tenant.name}</h2>
              <Badge variant={statusCfg.variant} size="sm" dot>
                {statusCfg.label}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-text-secondary">
              <code className="rounded bg-surface-secondary px-1.5 py-0.5 text-xs">
                {tenant.slug}
              </code>
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {canManageOrgs && tenant.status === 'ACTIVE' && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setConfirmAction('suspend')}
              >
                Suspend Organization
              </Button>
            )}
            {canManageOrgs && tenant.status === 'SUSPENDED' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmAction('activate')}
              >
                Activate Organization
              </Button>
            )}
            {canSetAccountKind && (
              <Button
                variant="secondary"
                size="sm"
                disabled={accountKindMutation.isPending}
                loading={accountKindMutation.isPending}
                onClick={() => void toggleAccountKind()}
              >
                {tenant.accountKind === 'HOUSE'
                  ? 'Mark as Merchant'
                  : 'Mark as House'}
              </Button>
            )}
          </div>
        </div>

        {/* Metadata grid */}
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MetadataItem label="Account type">
            {tenant.accountKind === 'HOUSE' ? (
              <Badge variant="warning">House</Badge>
            ) : (
              <Badge variant="default">Merchant</Badge>
            )}
          </MetadataItem>
          <MetadataItem label="Company Name">
            <span className="text-sm text-text-primary">
              {tenant.companyName ?? '—'}
            </span>
          </MetadataItem>
          <MetadataItem label="Company Email">
            <span className="text-sm text-text-primary">
              {tenant.companyEmail ?? '—'}
            </span>
          </MetadataItem>
          <MetadataItem label="Company Phone">
            <span className="text-sm text-text-primary">
              {tenant.companyPhone ?? '—'}
            </span>
          </MetadataItem>
          <MetadataItem label="Created">
            <span className="text-sm text-text-primary">
              {formatDate(tenant.createdAt)}
            </span>
          </MetadataItem>
        </div>
      </Card>

      {/* Plan & Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <Card>
          <CardHeader title="Plan" />
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Plan Name</span>
              <Badge variant="info">{tenant.plan.name}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Storage Limit</span>
              <span className="text-sm font-medium text-text-primary">
                {tenant.plan.storageLimit} MB
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Exports Limit</span>
              <span className="text-sm font-medium text-text-primary">
                {tenant.plan.exportsLimit}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Stores Limit</span>
              <span className="text-sm font-medium text-text-primary">
                {tenant.plan.storesLimit}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-secondary">Users Limit</span>
              <span className="text-sm font-medium text-text-primary">
                {tenant.plan.usersLimit}
              </span>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Statistics" />
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-border p-4 text-center">
              <p className="text-2xl font-bold text-text-primary">{tenant.membersCount}</p>
              <p className="mt-1 text-xs text-text-tertiary">Members</p>
            </div>
            <div className="rounded-lg border border-border p-4 text-center">
              <p className="text-2xl font-bold text-text-primary">{tenant.connectionsCount}</p>
              <p className="mt-1 text-xs text-text-tertiary">Connections</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Members table */}
      <Card>
        <CardHeader
          title="Members"
          description={`${tenant.members.length} member${tenant.members.length !== 1 ? 's' : ''} in this organization.`}
        />

        {tenant.members.length === 0 ? (
          <div className="mt-4 flex items-center justify-center py-8 text-sm text-text-tertiary">
            No members found.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {tenant.members.map((member) => (
                  <tr
                    key={member.id}
                    className="transition-colors hover:bg-surface-secondary"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-text-primary whitespace-nowrap">
                      {getMemberName(member)}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {member.user.email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge
                        variant={member.role.slug === 'owner' ? 'primary' : 'default'}
                        size="sm"
                      >
                        {member.role.name}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {member.isActive ? (
                        <Badge variant="success" size="sm" dot>
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="warning" size="sm" dot>
                          Invited
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                      {member.acceptedAt ? formatDate(member.acceptedAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {confirmAction && tenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setConfirmAction(null)}
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-foreground">
              {confirmAction === 'suspend'
                ? 'Suspend Organization'
                : 'Activate Organization'}
            </h3>
            <p className="mt-2 text-sm text-muted">
              {confirmAction === 'suspend'
                ? `Are you sure you want to suspend "${tenant.name}"? All members will lose access immediately.`
                : `Are you sure you want to activate "${tenant.name}"?`}
            </p>

            {(suspendMutation.error || activateMutation.error) && (
              <p className="mt-2 text-sm text-danger">
                {(suspendMutation.error || activateMutation.error)?.message ??
                  'An error occurred'}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setConfirmAction(null)}
                disabled={
                  suspendMutation.isPending || activateMutation.isPending
                }
              >
                Cancel
              </Button>
              <Button
                variant={confirmAction === 'suspend' ? 'danger' : 'primary'}
                onClick={() => {
                  if (confirmAction === 'suspend') {
                    suspendMutation.mutate();
                  } else {
                    activateMutation.mutate();
                  }
                }}
                disabled={
                  suspendMutation.isPending || activateMutation.isPending
                }
              >
                {suspendMutation.isPending || activateMutation.isPending
                  ? 'Processing...'
                  : confirmAction === 'suspend'
                    ? 'Suspend'
                    : 'Activate'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetadataItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
        {label}
      </p>
      <div>{children}</div>
    </div>
  );
}
